<template>
  <div class="locale-chooser" ref="el">
    <locale-button
      class="locale-chooser__button"
      :locale="currentLocale"
      :class="{ open: isOpen }"
      :aria-expanded="isOpen"
      :aria-controls="menuId"
      @click="toggle"
    />
    <transition name="locale-chooser">
      <menu v-show="isOpen" ref="options" class="locale-chooser__options" :id="menuId">
        <li v-for="loc of locales" :key="loc.key">
          <locale-button :locale="loc.key" @click="onSelect" />
        </li>
      </menu>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { v4 } from "uuid";
import { setLocale } from "@/i18n";
import LocaleButton from "./LocaleButton.vue";
import type { ValidLanguage } from "@/i18n/ValidLanguages";

const props = withDefaults(
  defineProps<{
    value?: string;
    setCurrent?: boolean;
  }>(),
  {
    setCurrent: true
  }
);

const emit = defineEmits<{
  (event: "update:value", value: string): void;
}>();

const menuId = v4();

const { locale, availableLocales, t } = useI18n();

const locales = computed(() => {
  const locales = availableLocales as ValidLanguage[];
  const result: {
    [locale: string]: { key: ValidLanguage; label: string };
  } = {};
  for (const locale of locales) {
    if (locale == currentLocale.value) continue;
    result[locale] = {
      key: locale,
      label: t(`locales.${locale}`)
    };
  }
  return result;
});

const currentLocale = computed(() => {
  if (props.value) return props.value as ValidLanguage;
  return locale.value as ValidLanguage;
});

function onSelect(locale: ValidLanguage) {
  emit("update:value", locale);
  if (props.setCurrent) {
    setLocale(locale);
  }
  close();
}

// ==============================
// Dropdown logic

const el = ref<HTMLElement | null>(null);
const isOpen = ref(false);
function open() {
  isOpen.value = true;
  window.addEventListener("click", closeOnPageClick, true);
}
function close() {
  isOpen.value = false;
  window.removeEventListener("click", closeOnPageClick, false);
}
function toggle() {
  if (isOpen.value) close();
  else open();
}

function closeOnPageClick(mouseEvent: MouseEvent) {
  // only handle clicks not on this element:
  if (el.value && mouseEvent.composedPath().includes(el.value)) {
    return;
  }
  close();
}
</script>

<style lang="scss">
.locale-chooser {
  display: inline-flex;
  position: relative;
  padding: 0 0.5em;
  justify-content: center;

  &__button {
    transition: transform 100ms ease-out;
    &.open {
      transform: scale3d(1.2, 1.2, 1);
    }
  }

  &__options {
    display: grid;
    grid-template-columns: 1fr;
    position: absolute;
    top: 100%;
    left: 50%;
    margin-top: 0.5em;
    transform: translateX(-50%);
    background-color: var(--color-background-deep);
    gap: 0.5em;
    padding: 0.5em 0.5em;
    box-shadow: 0 2px 4px var(--color-shadow-1);
    z-index: 1;
    list-style: none;
  }

  &-enter-active {
    animation: locale-chooser-in 0.25s;
  }
  &-leave-active {
    animation: locale-chooser-in 0.125s reverse;
  }
}
@keyframes locale-chooser-in {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(-3px);
  }
  100% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}
</style>
